import { Injectable } from "@angular/core";
import { NextPageService } from "@cvx/nextpage";
import { NextPageRoute } from "@cvx/nextpage";
import {
  BehaviorSubject,
  map,
  Observable,
  combineLatest,
  forkJoin,
  of,
  catchError,
} from "rxjs";
import { LoggedUserService } from "./common/logged-user.service";
import {
  GroupAccessGuard,
  GroupAccessData,
} from "../guards/group-access.guard";
import { GroupName, GroupUtils } from "../utils/group-names";
import { GroupMembershipCacheService } from "./group-membership-cache.service";

/**
 * Interface for group membership check results
 */
interface GroupMembershipResult {
  group: GroupName;
  isInGroup: boolean;
}

@Injectable({
  providedIn: "root",
})
export class RoutesService {
  private allowedPages: { pageName: string; subPageName: string }[] = [];
  private filteredRoutes: NextPageRoute[] = [];

  filteredRoutesLoaded = new BehaviorSubject<boolean>(false);
  constructor(
    private NP: NextPageService,
    private loggedUserService: LoggedUserService,
    private groupAccessGuard: GroupAccessGuard,
    private groupMembershipCache: GroupMembershipCacheService
  ) {}

  setFilteredRoutes(routes: NextPageRoute[]) {
    this.filteredRoutes = routes;
  }

  getFilteredRoutes(): NextPageRoute[] {
    return this.filteredRoutes;
  }
  public filterRoutes(routes: NextPageRoute[]): Observable<NextPageRoute[]> {
    // Get all unique individual group requirements from all routes
    const allIndividualGroups = this.extractGroupRequirements(routes);

    if (allIndividualGroups.length === 0) {
      // No group requirements found, return routes as-is
      return of(routes);
    }

    // Use cached group membership service for efficient batch checking of individual groups
    return this.groupMembershipCache
      .getIndividualGroupMemberships(allIndividualGroups)
      .pipe(
        map((individualGroupMembershipMap) => {
          // Filter routes based on group membership
          return this.filterRoutesByGroupMembership(
            routes,
            individualGroupMembershipMap
          );
        }),
        catchError((error) => {
          console.error(
            "Error checking group memberships for route filtering:",
            error
          );
          // On error, return all routes to avoid breaking navigation
          return of(routes);
        })
      );
  }
  /**
   * Extracts all unique group requirements from routes and their children
   * Now returns individual group names extracted from comma-separated GroupName constants
   */
  private extractGroupRequirements(routes: NextPageRoute[]): string[] {
    const groups = new Set<string>();

    const extractFromRoute = (route: NextPageRoute) => {
      const groupAccessData = route.data as GroupAccessData;
      if (groupAccessData?.requiredGroups?.length) {
        groupAccessData.requiredGroups.forEach((groupName) => {
          // Extract individual groups from comma-separated GroupName constants
          const individualGroups = GroupUtils.getIndividualGroups(groupName);
          individualGroups.forEach((group) => groups.add(group));
        });
      }

      if (route.children?.length) {
        route.children.forEach(extractFromRoute);
      }
    };

    routes.forEach(extractFromRoute);
    return Array.from(groups);
  }
  /**
   * Filters routes based on user's individual group membership
   */
  private filterRoutesByGroupMembership(
    routes: NextPageRoute[],
    individualGroupMembershipMap: Map<string, boolean>
  ): NextPageRoute[] {
    return routes
      .map((route) => {
        // Clone the route to avoid modifying the original
        const filteredRoute = { ...route };

        // Check if route has group access requirements
        const groupAccessData = route.data as GroupAccessData;
        if (groupAccessData?.requiredGroups?.length) {
          const hasAccess = this.checkRouteAccessWithIndividualMembership(
            groupAccessData,
            individualGroupMembershipMap
          );
          if (!hasAccess) {
            // User doesn't have access to this route
            return null;
          }
        }

        // Recursively filter children routes
        if (route.children && route.children.length > 0) {
          const filteredChildren = this.filterRoutesByGroupMembership(
            route.children,
            individualGroupMembershipMap
          );
          filteredRoute.children = filteredChildren.filter(
            (child) => child !== null
          );

          // If all children are filtered out and this route has no component, hide the parent too
          if (filteredRoute.children.length === 0 && !route.component) {
            return null;
          }
        }

        return filteredRoute;
      })
      .filter((route): route is NextPageRoute => route !== null);
  }

  /**
   * Checks if user has access to a route based on group requirements and membership map
   */
  private checkRouteAccessWithMembership(
    groupAccessData: GroupAccessData,
    groupMembershipMap: Map<GroupName, boolean>
  ): boolean {
    if (!groupAccessData.requiredGroups?.length) {
      return true; // No group requirements, allow access
    }

    const requireAllGroups = groupAccessData.requireAllGroups ?? false;
    const groupChecks = groupAccessData.requiredGroups.map(
      (group) => groupMembershipMap.get(group) ?? false
    );

    if (requireAllGroups) {
      // User must be in ALL required groups
      return groupChecks.every((hasAccess) => hasAccess);
    } else {
      // User must be in ANY of the required groups
      return groupChecks.some((hasAccess) => hasAccess);
    }
  }
  /**
   * Checks if user has access to a route based on individual group requirements and membership map
   */
  private checkRouteAccessWithIndividualMembership(
    groupAccessData: GroupAccessData,
    individualGroupMembershipMap: Map<string, boolean>
  ): boolean {
    if (!groupAccessData.requiredGroups?.length) {
      return true; // No group requirements, allow access
    }

    const requireAllGroups = groupAccessData.requireAllGroups ?? false;

    // For each GroupName constant, check if user is in any of its constituent groups
    const groupChecks = groupAccessData.requiredGroups.map((groupName) => {
      const individualGroups = GroupUtils.getIndividualGroups(groupName);
      // Check if user is in ANY of the individual groups for this GroupName constant
      return individualGroups.some(
        (group) => individualGroupMembershipMap.get(group) ?? false
      );
    });

    if (requireAllGroups) {
      // User must be in ALL required GroupName constants (each GroupName constant satisfied by ANY of its constituent groups)
      return groupChecks.every((hasAccess) => hasAccess);
    } else {
      // User must be in ANY of the required GroupName constants
      return groupChecks.some((hasAccess) => hasAccess);
    }
  }
  public filterRoutesForMenu(routes: NextPageRoute[]) {
    return this.filterRoutes(routes).subscribe({
      next: (filteredRoutes) => {
        this.setFilteredRoutes(filteredRoutes);
        this.NP.setOption("navigationRoutes", filteredRoutes);
        this.NP.UI.setNavigation(filteredRoutes);
        this.NP.options.navigationRoutes = filteredRoutes;
        this.NP.UI.options.navigationRoutes = filteredRoutes;
        this.NP.buildMenuStructure();
        this.filteredRoutesLoaded.next(true);
      },
      error: (error) => {
        console.error("Error filtering routes for menu:", error);
        // Fallback to original routes if filtering fails
        this.setFilteredRoutes(routes);
        this.NP.setOption("navigationRoutes", routes);
        this.NP.UI.setNavigation(routes);
        this.NP.options.navigationRoutes = routes;
        this.NP.UI.options.navigationRoutes = routes;
        this.NP.buildMenuStructure();
        this.filteredRoutesLoaded.next(true);
      },
    });
  }

  /**
   * Refreshes the filtered routes - useful when user permissions change
   * @param originalRoutes - The original unfiltered routes
   */
  public refreshFilteredRoutes(originalRoutes: NextPageRoute[]): void {
    this.filterRoutes(originalRoutes).subscribe((filteredRoutes) => {
      this.setFilteredRoutes(filteredRoutes);
      this.NP.setOption("navigationRoutes", filteredRoutes);
      this.NP.UI.setNavigation(filteredRoutes);
      this.NP.options.navigationRoutes = filteredRoutes;
      this.NP.UI.options.navigationRoutes = filteredRoutes;
      this.NP.buildMenuStructure();
      this.filteredRoutesLoaded.next(true);
    });
  }

  /**
   * Checks if a specific route should be visible to the current user
   * @param route - The route to check
   * @returns Observable<boolean> indicating if the route should be visible
   */
  public isRouteVisible(route: NextPageRoute): Observable<boolean> {
    const groupAccessData = route.data as GroupAccessData;

    if (!groupAccessData?.requiredGroups?.length) {
      return of(true); // No group requirements, always visible
    }

    const requireAllGroups = groupAccessData.requireAllGroups ?? false;

    if (requireAllGroups) {
      return this.groupAccessGuard.isUserInAllGroups(
        groupAccessData.requiredGroups
      );
    } else {
      return this.groupAccessGuard.isUserInAnyGroup(
        groupAccessData.requiredGroups
      );
    }
  }
}
